import React, { useEffect, useState } from "react"

import MonacoEditor from "@monaco-editor/react"
import { useRecoilState, useRecoilValue } from "recoil"
import { v4 as uuidv4 } from "uuid"

import { useTestData } from "@fuse/recoil/pluginStudio"
import { UserRole } from "@fuse/recoil/pluginStudio/types"

// Enhanced TestDataPanelProps to support user roles and code tab sync
interface TestDataPanelProps {
  onSelectUser?: (userData: any, role: UserRole) => void
  showTestCasesSection?: boolean // Add prop to control visibility of test cases section
  setPanelVisibility?: (visibility: boolean) => void
  onToggleVisibility?: () => void // Add callback for toggling panel visibility
  isVisible?: boolean // Whether panel is visible
  title?: string // Panel title
}

// Icons for user roles
const UserRoleIcons = {
  [UserRole.HOST_CONFIG_VIEW]: (
    <svg
      className="h-4 w-4 text-purple-400"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-5-9h10v2H7z" />
    </svg>
  ),
  [UserRole.HOST_EVENT_VIEW]: (
    <svg
      className="h-4 w-4 text-blue-400"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-11h2v6h-2zm0-4h2v2h-2z" />
    </svg>
  ),
  [UserRole.GUEST]: (
    <svg
      className="h-4 w-4 text-green-400"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M12 6c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2m0 10c2.7 0 5.8 1.29 6 2H6c.23-.72 3.31-2 6-2m0-12C9.79 4 8 5.79 8 8s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 10c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </svg>
  ),
}

/**
 * Presentation component for the test data panel
 */
export const TestDataPanelUI: React.FC<{
  title?: string
  onToggleVisibility?: () => void
  testCases: any[]
  activeTestCase: any
  liveData: any
  activeLiveUser: any
  hostConfigViewUsers: Set<string>
  isVisible?: boolean
  editingTabId: string | null
  tabName: string
  editingUserId: string | null
  userName: string
  editField: string | null
  editContext: any
  editValue: string
  onAddTestCase: () => void
  onSelectTestCase: (testCaseId: string, loadToLive: boolean) => void
  onDeleteTestCase: (testCaseId: string) => void
  onTabNameEdit: (tabId: string, name: string) => void
  onStartTabNameEdit: (tabId: string, currentName: string) => void
  onFinishTabNameEdit: (tabId: string) => void
  onAddUser: (role: UserRole) => void
  onSelectUser: (userId: string) => void
  onRemoveUser: (userId: string) => void
  onUserNameEdit: (userId: string, name: string) => void
  onStartUserNameEdit: (userId: string, currentName: string) => void
  onFinishUserNameEdit: (userId: string) => void
  onToggleUserhostConfigView: (userId: string) => void
  onEditFieldChange: (field: string, context: any) => void
  onEditValueChange: (value: string) => void
  onSaveEdit: () => void
  onCancelEdit: () => void
  onSyncToTestCase: () => void
  onResetFromTestCase: () => void
}> = ({
  title = "Test Cases",
  onToggleVisibility,
  testCases,
  activeTestCase,
  liveData,
  activeLiveUser,
  hostConfigViewUsers,
  isVisible = true,
  editingTabId,
  tabName,
  editingUserId,
  userName,
  editField,
  editContext,
  editValue,
  onAddTestCase,
  onSelectTestCase,
  onDeleteTestCase,
  onTabNameEdit,
  onStartTabNameEdit,
  onFinishTabNameEdit,
  onAddUser,
  onSelectUser,
  onRemoveUser,
  onUserNameEdit,
  onStartUserNameEdit,
  onFinishUserNameEdit,
  onToggleUserhostConfigView,
  onEditFieldChange,
  onEditValueChange,
  onSaveEdit,
  onCancelEdit,
  onSyncToTestCase,
  onResetFromTestCase,
}) => {
  // Move the entire render logic here from the existing component
  // Keep all the JSX and visual components

  // Update the role buttons to use UserRole enum
  const roleButtons = [
    { role: UserRole.GUEST, label: "Add Guest" },
    { role: UserRole.HOST_EVENT_VIEW, label: "Add Host" },
    { role: UserRole.HOST_CONFIG_VIEW, label: "Add Host Config View" },
  ]

  return (
    <div className="flex flex-col h-full">
      {/* Panel header */}
      <div className="px-3 py-2 bg-gray-800/80 border-b border-gray-700 flex items-center justify-between">
        <div className="flex items-center flex-1 overflow-x-auto">
          <span className="font-medium text-sm text-white mr-4">{title}</span>

          {/* Test case tabs inline with header */}
          <div className="flex items-center gap-2 overflow-x-auto py-1">
            {testCases.map((testCase) => (
              <div
                key={testCase.id}
                className={`relative px-3 py-1 text-xs rounded-md flex items-center gap-1.5 ${
                  activeTestCase?.id === testCase.id
                    ? "bg-gray-600 text-white"
                    : "bg-gray-700/50 text-gray-300 hover:bg-gray-700"
                } cursor-pointer group transition-colors`}
                onClick={() => onSelectTestCase(testCase.id, false)}
              >
                {editingTabId === testCase.id ? (
                  <input
                    type="text"
                    value={tabName}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => onTabNameEdit(testCase.id, e.target.value)}
                    onBlur={() => onFinishTabNameEdit(testCase.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        onFinishTabNameEdit(testCase.id)
                      }
                    }}
                    className="bg-transparent border-b border-gray-500 focus:outline-none w-24 text-xs"
                    autoFocus
                  />
                ) : (
                  <span
                    onDoubleClick={(e) => {
                      e.stopPropagation()
                      onStartTabNameEdit(testCase.id, testCase.name)
                    }}
                  >
                    {testCase.name}
                  </span>
                )}

                <div className="flex items-center gap-1 ml-1">
                  {activeTestCase?.id === testCase.id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        if (liveData.sourceTestCaseId === testCase.id) {
                          onResetFromTestCase()
                        } else {
                          onSelectTestCase(testCase.id, true)
                        }
                      }}
                      className="text-[10px] px-1.5 py-0.5 bg-gray-500 hover:bg-gray-400 text-white rounded opacity-80 hover:opacity-100"
                    >
                      {liveData.sourceTestCaseId === testCase.id
                        ? "Restart"
                        : "Load"}
                    </button>
                  )}

                  {/* Delete button - only shown on hover and if we have more than one test case */}
                  {testCases.length > 1 && (
                    <button
                      className="opacity-0 group-hover:opacity-70 hover:opacity-100 text-gray-400 hover:text-white"
                      onClick={(e) => {
                        e.stopPropagation()
                        onDeleteTestCase(testCase.id)
                      }}
                      title="Delete test case"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3 w-3"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}

            {/* Add test case button inline */}
            <button
              className="text-xs py-1 px-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded flex items-center"
              onClick={onAddTestCase}
              title="Add New Test Case"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3 mr-1"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 3a1 1 0 00-1 1v5H4a1 1 0 100 2h5v5a1 1 0 102 0v-5h5a1 1 0 100-2h-5V4a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              New
            </button>
          </div>
        </div>

        {/* Toggle visibility button */}
        {onToggleVisibility && (
          <button
            className="p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-white ml-2"
            onClick={onToggleVisibility}
            title="Hide Test Cases"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Panel content */}
      <div className="flex-1 overflow-y-auto bg-gray-800 text-gray-100">
        {/* Render JSON editor if active */}
        {editField && editContext && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg w-full max-w-4xl overflow-hidden">
              <div className="px-4 py-3 bg-gray-700 flex justify-between items-center">
                <h3 className="text-white font-medium">
                  Edit {editField.charAt(0).toUpperCase() + editField.slice(1)}
                </h3>
                <button
                  className="text-gray-300 hover:text-white"
                  onClick={onCancelEdit}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="h-[60vh]">
                <MonacoEditor
                  language="json"
                  theme="vs-dark"
                  value={editValue}
                  onChange={(value) => onEditValueChange(value || "")}
                  options={{
                    minimap: { enabled: false },
                    scrollBeyondLastLine: false,
                    fontSize: 14,
                  }}
                />
              </div>
              <div className="px-4 py-3 bg-gray-700 flex justify-end">
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded"
                  onClick={onSaveEdit}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Only show these sections if we have an active test case */}
        {activeTestCase && (
          <div className="p-3">
            {/* Sync notification - Only show when needed */}
            {liveData.isDirty &&
              liveData.sourceTestCaseId === activeTestCase.id && (
                <div className="mb-3 flex items-center justify-between p-2 bg-yellow-600/20 border border-yellow-600/30 rounded-md">
                  <span className="text-xs text-yellow-300">
                    Live data has been modified
                  </span>
                  <button
                    className="text-xs py-1 px-2 bg-yellow-600/70 hover:bg-yellow-500 rounded"
                    onClick={onSyncToTestCase}
                  >
                    Save Changes
                  </button>
                </div>
              )}

            {/* Test case sections */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-gray-900/70 p-3 rounded-md border border-gray-700">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-medium text-gray-300">
                    Event Data
                  </h3>
                  <button
                    className="text-xs py-1 px-2 bg-gray-700 hover:bg-gray-600 rounded"
                    onClick={() =>
                      onEditFieldChange("eventData", activeTestCase)
                    }
                  >
                    Edit
                  </button>
                </div>
                <pre className="text-xs text-gray-400 overflow-auto max-h-32">
                  {JSON.stringify(activeTestCase.eventData, null, 2) || "{}"}
                </pre>
              </div>

              <div className="bg-gray-900/70 p-3 rounded-md border border-gray-700">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-medium text-gray-300">
                    Plugin Config
                  </h3>
                  <button
                    className="text-xs py-1 px-2 bg-gray-700 hover:bg-gray-600 rounded"
                    onClick={() =>
                      onEditFieldChange("pluginConfig", activeTestCase)
                    }
                  >
                    Edit
                  </button>
                </div>
                <pre className="text-xs text-gray-400 overflow-auto max-h-32">
                  {JSON.stringify(activeTestCase.pluginConfig, null, 2) || "{}"}
                </pre>
              </div>
            </div>

            <div className="mb-4">
              <div className="bg-gray-900/70 p-3 rounded-md border border-gray-700">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-medium text-gray-300">
                    Event Plugin Instance Data
                  </h3>
                  <button
                    className="text-xs py-1 px-2 bg-gray-700 hover:bg-gray-600 rounded"
                    onClick={() =>
                      onEditFieldChange("eventPluginData", activeTestCase)
                    }
                  >
                    Edit
                  </button>
                </div>
                <pre className="text-xs text-gray-400 overflow-auto max-h-32">
                  {JSON.stringify(activeTestCase.eventPluginData, null, 2) ||
                    "{}"}
                </pre>
              </div>
            </div>

            {/* Users section */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-medium text-gray-300">Users</h3>
                <div className="flex gap-2">
                  {roleButtons.map(({ role, label }) => (
                    <button
                      key={role}
                      className="text-xs py-1 px-2 bg-gray-700 hover:bg-gray-600 rounded flex items-center gap-1"
                      onClick={() => onAddUser(role)}
                    >
                      {UserRoleIcons[role]}
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                {liveData.users.map((user) => (
                  <div
                    key={user.id}
                    className={`bg-gray-900/70 p-3 rounded-md border ${
                      activeLiveUser?.id === user.id
                        ? "border-blue-500"
                        : "border-gray-700"
                    } ${
                      activeLiveUser?.id === user.id
                        ? "ring-1 ring-blue-500"
                        : ""
                    }`}
                    onClick={() => onSelectUser(user.id)}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-2">
                        {user.role === UserRole.GUEST
                          ? UserRoleIcons[UserRole.GUEST]
                          : user.role === UserRole.HOST_EVENT_VIEW
                            ? UserRoleIcons[UserRole.HOST_EVENT_VIEW]
                            : UserRoleIcons[UserRole.HOST_CONFIG_VIEW]}
                        {editingUserId === user.id ? (
                          <input
                            type="text"
                            value={userName}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) =>
                              onUserNameEdit(user.id, e.target.value)
                            }
                            onBlur={() => onFinishUserNameEdit(user.id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                onFinishUserNameEdit(user.id)
                              }
                            }}
                            className="bg-transparent border-b border-gray-500 focus:outline-none text-sm w-24"
                            autoFocus
                          />
                        ) : (
                          <span
                            className="text-sm font-medium"
                            onDoubleClick={() =>
                              onStartUserNameEdit(user.id, user.name)
                            }
                          >
                            {user.name}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {user.role !== UserRole.GUEST && (
                          <button
                            className={`text-xs py-1 px-2 rounded ${
                              hostConfigViewUsers.has(user.id)
                                ? "bg-purple-600 hover:bg-purple-700 text-white"
                                : "bg-gray-700 hover:bg-gray-600"
                            }`}
                            onClick={(e) => {
                              e.stopPropagation()
                              onToggleUserhostConfigView(user.id)
                            }}
                          >
                            Config View
                          </button>
                        )}
                        <button
                          className="text-xs py-1 px-2 bg-red-600/30 hover:bg-red-600/50 text-red-200 rounded"
                          onClick={(e) => {
                            e.stopPropagation()
                            onRemoveUser(user.id)
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>

                    <div className="mt-2">
                      <div className="text-xs text-gray-500 mb-1">
                        Event Plugin User Data
                      </div>
                      <pre className="text-xs bg-gray-800 p-2 rounded whitespace-pre-wrap overflow-auto max-h-16">
                        {JSON.stringify(
                          user.userData || user.userPluginData || {},
                          null,
                          2,
                        )}
                      </pre>
                      <button
                        className="mt-2 text-xs py-1 px-2 bg-gray-700 hover:bg-gray-600 rounded w-full"
                        onClick={() => {
                          // Edit either userData or userPluginData depending on which has data
                          const hasUserData =
                            Object.keys(user.userData || {}).length > 0
                          const hasPluginData =
                            Object.keys(user.userPluginData || {}).length > 0

                          // If both have data, prefer userData, otherwise use whichever has data
                          onEditFieldChange(
                            hasUserData ? "userData" : "userPluginData",
                            user,
                          )
                        }}
                      >
                        Edit User Data
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Container component that connects to Recoil state
 */
const TestDataPanel: React.FC<TestDataPanelProps> = ({
  onSelectUser,
  showTestCasesSection = true,
  setPanelVisibility,
  onToggleVisibility,
  isVisible = true,
  title = "Test Cases",
}) => {
  // Connect to Recoil state
  const {
    testCases,
    activeTestCase,
    liveData,
    activeLiveUser,
    createTestCase,
    updateTestCase,
    deleteTestCase,
    loadTestCaseToLive,
    syncLiveDataToTestCase,
    resetLiveDataFromTestCase,
    setLiveActiveUser,
    updateLiveEventData,
    updateLivePluginConfig,
    updateLiveUser,
    addLiveUser,
    removeLiveUser,
  } = useTestData()

  // State for editor values
  const [editValue, setEditValue] = useState<string>("") // Current edited JSON value
  const [editField, setEditField] = useState<string | null>(null) // Field being edited
  const [editContext, setEditContext] = useState<any>(null) // Context for editing (testCase, user)

  // State for editing tab and user names
  const [editingTabId, setEditingTabId] = useState<string | null>(null)
  const [tabName, setTabName] = useState<string>("")

  const [editingUserId, setEditingUserId] = useState<string | null>(null)
  const [userName, setUserName] = useState<string>("")

  // State for tracking which users are in host config view
  const [hostConfigViewUsers, sethostConfigViewUsers] = useState<Set<string>>(
    new Set(),
  )

  // Initialize with at least one test case if none exist
  useEffect(() => {
    if (testCases.length === 0) {
      createTestCase()
    }
  }, [])

  // Update editor value when a field is selected for editing
  useEffect(() => {
    if (!editField || !editContext) return

    let value
    if (editField === "eventData") {
      value = editContext.eventData
    } else if (editField === "pluginConfig") {
      value = editContext.pluginConfig
    } else if (editField === "eventPluginData") {
      value = editContext.eventPluginData
    } else if (editField === "userData") {
      value = editContext.userData
    } else if (editField === "userPluginData") {
      value = editContext.userPluginData
    } else {
      value = {}
    }

    setEditValue(JSON.stringify(value, null, 2))
  }, [editField, editContext])

  const handleSaveEdit = () => {
    if (!editField || !editContext) return

    try {
      const parsedValue = JSON.parse(editValue)

      if (editField === "eventData") {
        updateLiveEventData(parsedValue)
      } else if (editField === "pluginConfig") {
        updateLivePluginConfig(parsedValue)
      } else if (editField === "eventPluginData") {
        updateTestCase(editContext.id, {
          eventPluginData: parsedValue,
        })
      } else if (editField === "userData" && editContext.id) {
        updateLiveUser(editContext.id, {
          userData: parsedValue,
        })
      } else if (editField === "userPluginData" && editContext.id) {
        updateLiveUser(editContext.id, {
          userPluginData: parsedValue,
        })
      }

      // Clear editing state after successful save
      setEditField(null)
      setEditContext(null)
    } catch (error) {
      console.error("Failed to parse JSON:", error)
    }
  }

  return (
    <TestDataPanelUI
      title={title}
      onToggleVisibility={onToggleVisibility}
      testCases={testCases}
      activeTestCase={activeTestCase}
      liveData={liveData}
      activeLiveUser={activeLiveUser}
      hostConfigViewUsers={hostConfigViewUsers}
      isVisible={isVisible}
      editingTabId={editingTabId}
      tabName={tabName}
      editingUserId={editingUserId}
      userName={userName}
      editField={editField}
      editContext={editContext}
      editValue={editValue}
      onAddTestCase={createTestCase}
      onSelectTestCase={(testCaseId, loadToLive) => {
        updateTestCase(testCaseId, {})
        if (loadToLive) {
          loadTestCaseToLive(testCaseId)
        }
      }}
      onDeleteTestCase={deleteTestCase}
      onTabNameEdit={(tabId, name) => setTabName(name)}
      onStartTabNameEdit={(tabId, currentName) => {
        setEditingTabId(tabId)
        setTabName(currentName)
      }}
      onFinishTabNameEdit={(tabId) => {
        if (tabName.trim() && editingTabId) {
          updateTestCase(tabId, { name: tabName.trim() })
        }
        setEditingTabId(null)
      }}
      onAddUser={(role) => {
        addLiveUser(role)
        if (onSelectUser) {
          const selectedUser = liveData.users.find((user) => user.role === role)
          if (selectedUser) {
            onSelectUser(selectedUser.userData, role)
          }
        }
      }}
      onSelectUser={(userId) => {
        setLiveActiveUser(userId)
        const selectedUser = liveData.users.find((user) => user.id === userId)
        if (selectedUser && onSelectUser) {
          const role = hostConfigViewUsers.has(userId)
            ? UserRole.HOST_CONFIG_VIEW
            : selectedUser.role
          onSelectUser(selectedUser.userData, role)
        }
      }}
      onRemoveUser={removeLiveUser}
      onUserNameEdit={(userId, name) => setUserName(name)}
      onStartUserNameEdit={(userId, currentName) => {
        setEditingUserId(userId)
        setUserName(currentName)
      }}
      onFinishUserNameEdit={(userId) => {
        if (userName.trim() && editingUserId) {
          updateLiveUser(userId, {
            name: userName.trim(),
            userData: {
              ...liveData.users.find((u) => u.id === userId)?.userData,
              displayName: userName.trim(),
            },
          })
        }
        setEditingUserId(null)
      }}
      onToggleUserhostConfigView={(userId) => {
        sethostConfigViewUsers((prev) => {
          const newSet = new Set(prev)
          if (newSet.has(userId)) {
            newSet.delete(userId)
          } else {
            newSet.add(userId)
          }
          return newSet
        })
      }}
      onEditFieldChange={(field, context) => {
        setEditField(field)
        setEditContext(context)
      }}
      onEditValueChange={setEditValue}
      onSaveEdit={handleSaveEdit}
      onCancelEdit={() => {
        setEditField(null)
        setEditContext(null)
      }}
      onSyncToTestCase={syncLiveDataToTestCase}
      onResetFromTestCase={resetLiveDataFromTestCase}
    />
  )
}

export default TestDataPanel
